import {AreaDispatcher, isSavingAllowed} from '@gaia/hooks/useAreas';
import useIsSaved from '@gaia/hooks/useIsSaved';
import type {TrackDispatcher} from '@gaia/hooks/useTracks';
import type {WaypointDispatcher} from '@gaia/hooks/useWaypoints';
import {useUser} from '@gaia/stores/user';
import type {ImportState, SharedData} from '@gaia/types';
import getFolderMemberships from '@gaia/utils/gaia/getFolderMemberships';
import saveFolder, {getIsFolderModifiedSinceSave} from '@gaia/utils/gaia/saveFolder';
import {useCallback, useEffect, useState} from 'react';

import bulkArchive from './dispatchers/bulkArchive';
import create from './dispatchers/create';
import createImportFolder from './dispatchers/createImportFolder';
import remove from './dispatchers/remove';
import takeLocal from './dispatchers/takeLocal';
import update from './dispatchers/update';
import updateProperties from './dispatchers/updateProperties';
export type FolderDispatcher = (action: FolderAction) => string[];

interface UseFoldersProps {
    areas: Record<string, Area>;
    waypoints: Record<string, Waypoint>;
    tracks: Record<string, Track>;
    sharedData: SharedData;
    interactionType: InteractionState['type'];
    importState: ImportState;
    areaDispatcher: AreaDispatcher;
    trackDispatcher: TrackDispatcher;
    waypointDispatcher: WaypointDispatcher;
}

export function useFolders({
    areas,
    waypoints,
    tracks,
    sharedData,
    interactionType,
    importState,
    areaDispatcher,
    trackDispatcher,
    waypointDispatcher,
}: UseFoldersProps): [{[localId: string]: Folder}, FolderDispatcher, number] {
    const {isAuthenticated} = useUser();

    const [folders, setFolders] = useState<{[folderLocalId: string]: Folder}>({});

    const [unsaved, addSavePromise] = useIsSaved();

    // The relationship between items and folders is implicit and recomputed as needed
    const folderMemberships = getFolderMemberships(folders, areas, waypoints, tracks);

    const dispatcher = useCallback(
        (action: FolderAction): string[] => {
            if (action.type === 'create') {
                return create(action, setFolders);
            } else if (action.type === 'createImportFolder') {
                // Unlike the other actions this will always return an empty array since it is async
                return createImportFolder(action, setFolders);
            } else if (action.type === 'update') {
                return update(action, setFolders);
            } else if (action.type === 'takeLocal') {
                return takeLocal(action, folderMemberships, setFolders, sharedData);
            } else if (action.type === 'remove') {
                return remove(action, setFolders);
            } else if (action.type === 'updateProperties') {
                return updateProperties(action, setFolders);
            } else if (action.type === 'bulkArchive') {
                return bulkArchive(
                    action,
                    setFolders,
                    areaDispatcher,
                    trackDispatcher,
                    waypointDispatcher
                );
            } else {
                throw new Error('Unknown action type');
            }
        },
        [folderMemberships, sharedData, areaDispatcher, trackDispatcher, waypointDispatcher]
    );

    useEffect(() => {
        if (isSavingAllowed(interactionType, importState, isAuthenticated)) {
            for (const localId in folderMemberships) {
                const folder = folderMemberships[localId];
                if (getIsFolderModifiedSinceSave(folder)) {
                    addSavePromise(saveFolder(folder, dispatcher));
                }
            }
        }
    }, [
        addSavePromise,
        folderMemberships,
        dispatcher,
        importState,
        interactionType,
        isAuthenticated,
    ]);

    return [folderMemberships, dispatcher, unsaved];
}
